import { Link } from "react-router-dom";
import { ArrowRight, FileCheck, Building2, BookOpen } from "lucide-react";

interface Props {
  onIrA: (tab: "tutoriales" | "faq" | "contacto" | "asistencia") => void;
}

export function AyudaGeneralTab({ onIrA }: Props) {
  return (
    <div className="space-y-4">
      <section className="overflow-hidden rounded-lg border bg-surface shadow-card">
        <div className="border-b px-5 py-3">
          <h2 className="text-base font-semibold text-fg">Bienvenido a SICEN</h2>
          <p className="mt-1 text-sm text-fg-muted">
            Mira la introducción institucional al Sistema de Certificaciones de Normalización
            Profesional de México (NPMX) — organismo certificador acreditado por la EMA y
            autorizado por el IFT.
          </p>
        </div>
        <div className="bg-black">
          <video
            controls
            preload="metadata"
            poster="/assets/logo-NPMX.png"
            className="mx-auto block max-h-[480px] w-full bg-black"
          >
            <source src="/assets/NPMX.mp4" type="video/mp4" />
            Tu navegador no admite la reproducción de video.
          </video>
        </div>
      </section>

      <section className="rounded-lg border bg-surface p-5 shadow-card">
        <h2 className="text-base font-semibold text-fg">¿Qué es SICEN?</h2>
        <p className="mt-2 text-sm leading-relaxed text-fg-muted">
          SICEN es la plataforma digital de NPMX para gestionar el ciclo completo de
          certificaciones: alta y validación del Registro Legal de la empresa, captura de
          solicitudes por sector, evaluación técnica, revisión, toma de decisión y emisión
          del certificado oficial. Está dirigida a clientes externos (titulares, tramitadores
          y representantes legales) y al personal interno del organismo (Calidad, Telecom,
          Eléctrica, Eficiencia, Auditoría, Dirección y Facturación).
        </p>
      </section>

      <section className="rounded-lg border bg-surface p-5 shadow-card">
        <h2 className="text-base font-semibold text-fg">¿Por dónde empiezo?</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <PasoCard
            n={1}
            icon={<Building2 className="h-5 w-5" />}
            titulo="Da de alta tu Registro Legal"
            texto="Captura los datos jurídicos de la empresa y carga los documentos requeridos para que Calidad apruebe tu RL."
            cta="Ver tutorial de RL"
            to="/ayuda/registro-legal"
          />
          <PasoCard
            n={2}
            icon={<FileCheck className="h-5 w-5" />}
            titulo="Crea una solicitud por sector"
            texto="Una vez aprobado el RL y habilitado el sector, captura la solicitud, súbela y dale seguimiento por estado."
            cta="Tutoriales por módulo"
            onClick={() => onIrA("tutoriales")}
          />
          <PasoCard
            n={3}
            icon={<BookOpen className="h-5 w-5" />}
            titulo="Consulta el avance"
            texto="Sigue el estado de tus solicitudes en el módulo correspondiente. La campana del encabezado te muestra cuántas tienes en proceso."
            cta="Preguntas frecuentes"
            onClick={() => onIrA("faq")}
          />
          <PasoCard
            n={4}
            icon={<ArrowRight className="h-5 w-5" />}
            titulo="¿Necesitas ayuda específica?"
            texto="Si la información del centro no resuelve tu duda, contáctanos por correo, teléfono o WhatsApp institucional."
            cta="Contáctanos"
            onClick={() => onIrA("contacto")}
          />
        </div>
      </section>
    </div>
  );
}

function PasoCard({
  n, icon, titulo, texto, cta, to, onClick,
}: {
  n: number;
  icon: React.ReactNode;
  titulo: string;
  texto: string;
  cta: string;
  to?: string;
  onClick?: () => void;
}) {
  const cuerpo = (
    <>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-fg-subtle">
        <span className="grid h-6 w-6 place-items-center rounded-full bg-primary-tint text-primary-strong">{icon}</span>
        Paso {n}
      </div>
      <h3 className="mt-2 text-sm font-semibold text-fg">{titulo}</h3>
      <p className="mt-1 text-xs text-fg-muted">{texto}</p>
      <div className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary-strong">
        {cta} <ArrowRight className="h-3 w-3" />
      </div>
    </>
  );
  const cls = "block rounded-lg border bg-surface-2 p-4 text-left transition hover:border-primary-strong hover:bg-primary-tint";
  return to
    ? <Link to={to} className={cls}>{cuerpo}</Link>
    : <button type="button" onClick={onClick} className={`${cls} w-full`}>{cuerpo}</button>;
}
